import React, { useContext, useEffect, useState } from 'react';
import { Card, Table, Spin, Empty, Typography } from 'antd';
import dayjs from 'dayjs';
import api from '../../api';
import { UserContext } from '../../App';

/**
 * The leaderboard's measures for this campaign, a row per month.
 *
 * The leaderboard is a row per agent and ranks them against each other. Here
 * there is one campaign, so a rank has nobody to rank against -- the goal
 * takes its place under each figure. It says whether the month met what was
 * asked of it, which is the question this page is for.
 *
 * Rolling agent rows up is not summing them: the averages are weighted by the
 * contacts they were taken over and adherence is recomputed from total hours,
 * both server-side. See the endpoint for why.
 */

const fmtSecs = (v) => {
  if (v == null) return '—';
  const m = Math.floor(v / 60);
  const s = Math.round(v % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};
const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtHrs = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }));
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

// Lower is better for handle time and errors, higher for score and adherence.
const DIRECTION = {
  AvgTalkSecs: 'lower', AvgACWSecs: 'lower', AvgHTSecs: 'lower', ErrorPct: 'lower',
  AvgQAScore: 'higher', UpsellCount: 'higher', AdherencePct: 'higher',
};

function met(metric, value, target) {
  if (value == null || target == null) return null;
  return DIRECTION[metric] === 'lower' ? value <= target : value >= target;
}

/**
 * A figure with its goal beneath it, in place of the leaderboard's rank.
 * No goal set means no second line at all -- an empty one would read as a
 * goal of zero that was missed.
 */
function WithGoal({ metric, value, goals, fmt, scaleBy }) {
  const raw = goals?.[metric]?.Target;
  // Some goals are set per agent rather than per campaign. Upsells is one: a
  // goal of 10 against a campaign of 24 agents is a campaign goal of 240, and
  // showing 10 beside a total of 42 made a good month look like a miss.
  const target = raw == null ? null
    : scaleBy == null ? raw
    : scaleBy === 0 ? null
    : raw * scaleBy;
  const hit = met(metric, value, target);
  return (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ color: hit === false ? '#cf1322' : hit === true ? '#389e0d' : undefined }}>
        {fmt(value)}
      </div>
      {target != null && (
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          goal {fmt(target)}
        </Typography.Text>
      )}
    </div>
  );
}

export default function MonthlyScorecard({ campaignId }) {
  // Columns follow the viewer's access: each measure needs its area's.
  const currentUser = useContext(UserContext);
  const lvl = (k) => currentUser?.[k] ?? 0;
  const can = {
    evals:      lvl('CanEditEvals') >= 1,
    errors:     lvl('CanEditErrors') >= 1,
    compliance: lvl('CanEditIncidents') >= 1,
    coaching:   lvl('CanEditCoaching') >= 1,
    absences:   lvl('CanEditAbsences') >= 1,
    oversight:  lvl('CanViewOversightStats') >= 1,
  };
  // Six months, fixed. This is a trend table -- one row a month -- and it
  // reads AgentMonthlyStats, which is small enough that the span costs
  // little. It does not take the page's range because the Overview tab hides
  // that control.
  const to   = dayjs().format('YYYY-MM-DD');
  const from = dayjs().subtract(5, 'month').startOf('month').format('YYYY-MM-DD');
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!campaignId) { setRows([]); return; }
    setLoading(true);
    api.get('/api/campaign-summary/leaderboard-monthly', {
      params: { campaign_id: campaignId, start_date: from, end_date: to },
    })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [campaignId, from, to]);

  // scaleKey names a column whose value multiplies the goal, for goals that
  // are set per agent rather than per campaign.
  const goalCol = (title, metric, fmt, width, scaleKey) => ({
    title, key: metric, width, align: 'right',
    sorter: (a, b) => (a[metric] ?? 0) - (b[metric] ?? 0),
    render: (_, r) => (
      <WithGoal
        metric={metric}
        value={r[metric]}
        goals={r.Goals}
        fmt={fmt}
        scaleBy={scaleKey ? r[scaleKey] : undefined}
      />
    ),
  });
  const plainCol = (title, key, fmt, width) => ({
    title, dataIndex: key, key, width, align: 'right',
    sorter: (a, b) => (a[key] ?? 0) - (b[key] ?? 0),
    render: fmt,
  });

  const columns = [
    { title: 'Month', dataIndex: 'MonthKey', key: 'MonthKey', width: 90, fixed: 'left',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') },
    plainCol('Agents', 'Agents', fmtNum, 80),
    {
      title: 'Contact Stats (Inbound)',
      children: [
        plainCol('Calls',  'InboundCalls',  fmtNum, 85),
        plainCol('Chats',  'InboundChats',  fmtNum, 85),
        plainCol('Total',  'TotalContacts', fmtNum, 90),
        goalCol('Avg Talk', 'AvgTalkSecs', fmtSecs, 95),
        goalCol('Avg ACW',  'AvgACWSecs',  fmtSecs, 95),
        goalCol('Avg HT',   'AvgHTSecs',   fmtSecs, 95),
      ],
    },
    {
      title: 'QA & Compliance',
      children: [
        // Occurrences: sections missed on evaluations -- QA.
        ...(can.evals ? [
          plainCol('QA Evals', 'EvalCount', fmtNum, 90),
          goalCol('QA Score',  'AvgQAScore', fmtPct, 95),
          plainCol('Occurrences', 'OccurrenceCount', fmtNum, 110),
        ] : []),
        ...(can.errors ? [
          plainCol('Errors', 'ErrorCount', fmtNum, 85),
          goalCol('Errors %', 'ErrorPct', (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`), 95),
        ] : []),
        ...(can.compliance ? [plainCol('Compliance', 'ComplianceCount', fmtNum, 105)] : []),
        ...(can.coaching ? [plainCol('Recoach', 'RecoachingCount', fmtNum, 95)] : []),
      ],
    },
    {
      title: 'Timecard & Schedule',
      children: [
        ...(can.oversight ? [
          plainCol('Worked',    'WorkedHrs',    fmtHrs, 90),
          plainCol('PTO',       'PTOHrs',       fmtHrs, 80),
          plainCol('Other',     'OtherHrs',     fmtHrs, 80),
          plainCol('Scheduled', 'ScheduledHrs', fmtHrs, 100),
          goalCol('Adherence',  'AdherencePct', fmtPct, 100),
        ] : []),
        ...(can.absences ? [plainCol('Absence Notifications', 'ShortTermNoticeCount', fmtNum, 110)] : []),
      ],
    },
    ...(rows.some((r) => r.UpsellCount != null) ? [{
      title: 'Upsells',
      children: [goalCol('Count', 'UpsellCount', fmtNum, 110, 'UpsellAgents')],
    }] : []),
  ].filter((c) => !c.children || c.children.length);

  return (
    <Card
      title={(
        <span>
          By month
          {from && to && (
            <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
              {from} to {to}
            </Typography.Text>
          )}
        </span>
      )}
      size="small"
      style={{ marginTop: 16 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : !rows.length ? (
        <Empty description="No monthly stats for this campaign in this window" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          rowKey="MonthKey"
          dataSource={rows}
          columns={columns}
          size="small"
          bordered
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  );
}
